'use client'

import type { Query, QueryClient } from '@tanstack/react-query'
import {
  classifyConnectivityError,
  getConnectivityAlertTitle,
  getConnectivityErrorMessage,
  getMutationFailureToast,
} from '@/lib/queryNetwork'
import {
  resolveConnectivityResource,
  resolveConnectivityResourceFromMutationKey,
} from '@/lib/connectivity/resources'
import {
  clearConnectivityResourceMutationError,
  patchConnectivityResource,
} from '@/lib/connectivity/store'
import { setConnectivityQueryClient } from '@/lib/connectivity/queryClientRef'

function aggregateResourceQueries(queries: Query[]) {
  const isFetching = queries.some((q) => q.state.fetchStatus === 'fetching')
  const failedQueries = queries.filter((q) => q.state.status === 'error')
  const primaryFailure = failedQueries[0]

  if (!primaryFailure) {
    return {
      isFetching,
      hasCachedData: false,
      errorKind: null,
      errorTitle: null,
      errorMessage: null,
    }
  }

  const error = primaryFailure.state.error
  const errorKind = classifyConnectivityError(error)
  const label = resolveConnectivityResource(primaryFailure.queryKey)?.label ?? 'data'
  const hasCachedData = failedQueries.some((q) => q.state.data !== undefined)

  return {
    isFetching,
    hasCachedData,
    errorKind,
    errorTitle: getConnectivityAlertTitle(errorKind, label),
    errorMessage: getConnectivityErrorMessage(error, `Failed to load ${label}.`),
  }
}

function syncResourceFromQueries(client: QueryClient, resourceId: string, label: string) {
  const queries = client
    .getQueryCache()
    .getAll()
    .filter((query) => resolveConnectivityResource(query.queryKey)?.id === resourceId)

  if (queries.length === 0) {
    patchConnectivityResource(resourceId, label, {
      isFetching: false,
      hasCachedData: false,
      errorKind: null,
      errorTitle: null,
      errorMessage: null,
    })
    return
  }

  const aggregate = aggregateResourceQueries(queries)
  patchConnectivityResource(resourceId, label, aggregate)
}

function syncQueryResource(client: QueryClient, query: Query) {
  const resource = resolveConnectivityResource(query.queryKey)
  if (!resource) return
  syncResourceFromQueries(client, resource.id, resource.label)
}

function syncAllResources(client: QueryClient) {
  const resourceIds = new Set<string>()
  for (const query of client.getQueryCache().getAll()) {
    const resource = resolveConnectivityResource(query.queryKey)
    if (resource) resourceIds.add(resource.id)
  }
  for (const resourceId of resourceIds) {
    const label =
      resolveConnectivityResource(
        client
          .getQueryCache()
          .getAll()
          .find((q) => resolveConnectivityResource(q.queryKey)?.id === resourceId)?.queryKey ?? []
      )?.label ?? resourceId
    syncResourceFromQueries(client, resourceId, label)
  }
}

export function attachConnectivityToQueryClient(client: QueryClient) {
  setConnectivityQueryClient(client)
  const queryCache = client.getQueryCache()
  const mutationCache = client.getMutationCache()

  queryCache.subscribe((event) => {
    if (
      event.type === 'added' ||
      event.type === 'updated' ||
      event.type === 'removed'
    ) {
      if (event.type === 'removed') {
        syncAllResources(client)
        return
      }
      syncQueryResource(client, event.query)
    }
  })

  mutationCache.subscribe((event) => {
    if (event.type !== 'updated') return

    const mutation = event.mutation
    const resource =
      resolveConnectivityResourceFromMutationKey(mutation.options.mutationKey) ??
      (mutation.options.meta &&
      typeof mutation.options.meta === 'object' &&
      'connectivityResourceId' in mutation.options.meta &&
      typeof mutation.options.meta.connectivityResourceId === 'string'
        ? {
            id: mutation.options.meta.connectivityResourceId,
            label:
              typeof mutation.options.meta.connectivityResourceLabel === 'string'
                ? mutation.options.meta.connectivityResourceLabel
                : mutation.options.meta.connectivityResourceId,
          }
        : null)

    if (!resource) return

    if (mutation.state.status === 'pending') {
      clearConnectivityResourceMutationError(resource.id)
      return
    }

    if (mutation.state.status !== 'error') {
      clearConnectivityResourceMutationError(resource.id)
      return
    }

    const actionPhrase =
      typeof mutation.options.meta === 'object' &&
      mutation.options.meta &&
      'connectivityActionPhrase' in mutation.options.meta &&
      typeof mutation.options.meta.connectivityActionPhrase === 'string'
        ? mutation.options.meta.connectivityActionPhrase
        : 'complete this action'

    const { title, description } = getMutationFailureToast(mutation.state.error, actionPhrase)
    patchConnectivityResource(resource.id, resource.label, {
      lastMutationErrorTitle: title,
      lastMutationErrorMessage: description,
    })
  })

  syncAllResources(client)
}

export function refetchConnectivityResource(
  client: QueryClient,
  resourceId: string
): Promise<void> {
  return client
    .refetchQueries({
      predicate: (query) => resolveConnectivityResource(query.queryKey)?.id === resourceId,
    })
    .then(() => undefined)
}
